import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import { OrderAddressInfo, OrderTypeEnum } from '../../../models/orders-type.model';
import {
    OpenCancelOrderModal,
    OpenGiftTicketModal,
    OpenOrderRefundModal,
    PrintEgui,
    RepayOrder,
    SetSubOrderSelected,
} from '../../../store/orders-type.actions';
import {
    selectDetailEcoTicketUrl,
    selectDetailInvoiceReturnUrl,
    selectDetailInvoiceUrl,
    selectDetailIsCanceled,
    selectDetailOrderAddress,
    selectDetailPayment,
    selectDetailOrder,
    selectDetailShowEcoTicket,
    selectItemsSubOrderSelected,
    selectOrderType,
    selectSubOrderSelected,
} from '../../../store/orders-type.selectors';
import { ItemOrderService } from '../item-order.service';

import { CartItem, CartItemOptions, CartItemTypes } from '@api/cart/cart.models';
import { Item } from '@api/catalog/items/medias/items.models';
import { ListTicketlessDocument, Order } from '@api/order/order.models';
import { OrderService } from '@api/order/order.service';
import { MarketConfigurable } from '@core/domain/impl/market-configurable.impl';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { getAllowedReturnStatus, getCmsConfigValue, getCountryCode, getCurrencyInfo, getShowReturnRequest, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { StaticResourceService } from '@core/services/static-resource.service';
import { SidebarPublicPort, SidebarPublicModels } from '@features/sidebar';
import { LazyLoadLegacyService } from '@lazy-load-legacy.service';
import { TransversalEventsService } from '@shared/common/services/transversal-events.service';
import { GoToInternalPage } from '@shared/navigation/store/navigation.actions';
import { PricePipe } from '@shared/pipes/price.pipe';
import { showGiftTicket } from '@shared/shopcart/store/shopcart.selectors';
import { getUserEmail, selectUserPhone } from '@shared/user/store';

@Component({
    selector: 'item-order-detail',
    templateUrl: './item-order-detail.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemOrderDetailComponent extends MarketConfigurable<Record<string, boolean>> implements OnInit {
    protected readonly config: Record<string, Record<string, boolean>> = {
        CN: {
            activeWechat: true,
        },
        TW: {
            activeEgui: true,
        },
        BE: {
            activeBancontact: true,
        },
        SE: {
            activeSwish: true,
        },
        UZ: {
            activeCODReturnMessage: true,
        },
    };

    @Input() public order: Partial<Order>;

    @Input() public hasSubOrders: boolean;

    @Input() public openedDetail: boolean = false;

    public mappedOrder: Order;

    public isReturnable = false;

    public readonly itemType: CartItemOptions = { name: CartItemTypes.ITEM_MY_ACCOUNT };

    public readonly fromOnline$: Observable<boolean> = this.store.select(selectOrderType)
        .pipe(map((orderType) => this.openedDetail ? true : orderType === OrderTypeEnum.ONLINE));

    public readonly subOrderSelected$: Observable<number> = this.store.select(selectSubOrderSelected);

    public readonly itemsByOrderSelected$: Observable<CartItem[]> = this.store.select(selectItemsSubOrderSelected)
        .pipe(map((items) => items as CartItem[]));

    public readonly paymentName$: Observable<string> = this.store.select(selectDetailPayment);

    public readonly orderIsCanceled$: Observable<boolean> = this.store.select(selectDetailIsCanceled);

    public readonly haveEmail$: Observable<boolean> = this.store.select(getUserEmail).pipe(map((email) => email.includes('@')));

    public readonly userEmail$: Observable<string> = this.store.select(getUserEmail);

    public readonly userPhone$: Observable<string> = this.store.select(selectUserPhone);

    public readonly orderAddress$: Observable<OrderAddressInfo> = this.store.select(selectDetailOrderAddress);

    public readonly showDownloadECOTicket$: Observable<boolean> = this.store.select(selectDetailShowEcoTicket);

    public readonly ecoTicketURL$: Observable<string> = this.store.select(selectDetailEcoTicketUrl);

    public readonly invoiceUrl$: Observable<string> = this.store.select(selectDetailInvoiceUrl);

    public readonly invoiceReturnUrl$: Observable<string> = this.store.select(selectDetailInvoiceReturnUrl);

    private readonly marketConfig$ = this.store.select(getCountryCode).pipe(
        map((countryCode) => this.getConfigByMarketCode(countryCode))
    );

    public readonly showWechat$ = this.marketConfig$.pipe(
        map((config) => config.activeWechat && this.order.payment?.[0]?.kind === 'Wechat_QR')
    );

    public readonly showBancontactQr$ = this.marketConfig$.pipe(
        map((config) => config.activeBancontact && this.order.payment?.[0]?.kind === 'Bancontact_QR')
    );

    public readonly showSwishQr$ = this.marketConfig$.pipe(
        map((config) => config.activeSwish && this.order.payment?.[0]?.kind === 'DirectSelection')
    );

    public readonly showEgui$ = this.marketConfig$.pipe(
        map((config) => this.order.egui ? config.activeEgui : false)
    );

    public readonly showCODReturnMessage$ = this.marketConfig$.pipe(
        map((config) => config.activeCODReturnMessage && this.order.payment?.[0]?.kind === 'cod')
    );

    public readonly printEguiAllowed$ = this.store.select(selectDetailOrder).pipe(
        map((orderDetails) => orderDetails.isEguiPrintAllowed)
    );

    public readonly isChangeable$ = this.store.select(getCmsConfigValue('items_changes_active'));

    public readonly multidevo$ = this.store.select(getCmsConfigValue('devos_multipedido'));

    public readonly showGiftTicketModal$: Observable<boolean> = this.showGiftTicketModal();

    public wechatKeys = {
        mainText: ['legacy.ItxMyAccountWechat.header'],
        pendingText: ['legacy.WechatQr.part1', 'legacy.WechatQr.part2'],
        cancelledText: ['legacy.ItxMyAccountWechat.cancelled'],
    };

    public bancontactKeys = {
        mainText: ['dev.checkout.confirmation.bancontactqr.txt1'],
        pendingText: ['dev.checkout.confirmation.bancontactqr.txt2', 'dev.checkout.confirmation.bancontactqr.txt3'],
        cancelledText: ['dev.bancontactqr.cancelled'],
    };

    public swishKeys = {
        mainText: ['dev.checkout.confirmation.swish.txt1'],
        pendingText: ['dev.checkout.confirmation.swish.txt2', 'dev.checkout.confirmation.swish.txt3'],
        cancelledText: ['dev.swish.cancelled'],
    };

    public image: string;

    public cancelled: boolean;

    public pending: boolean;

    public qr: string;

    public total$ = this.store.select(getCurrencyInfo).pipe(
        map((currencyInfo) => this.pricePipe.transform(this.order.totalOrder, currencyInfo))
    );

    // -----------------------------------------------------------------

    // TODO ECOMDUTI-21039 [TechDebt][SSR] - Tipar
    public storeOrderTicketUrl$: Observable<any>;

    public pdfList: boolean = false;

    public isCancellableAllOrder: boolean = false;

    public isCancellableSubOrder: boolean = false;

    constructor(
        private readonly store: Store,
        private readonly orderService: OrderService,
        private readonly configService: ConfigService,
        private readonly transversalEventsService: TransversalEventsService,
        private readonly lazyLoadLegacyService: LazyLoadLegacyService,
        private readonly itemOrderService: ItemOrderService,
        private readonly staticResourceService: StaticResourceService,
        private readonly pricePipe: PricePipe,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.image = this.staticResourceService.getPaymentLogo(this.order.payment?.[0]);

        this.cancelled = OrderService.isCancelled(this.order);

        this.pending = OrderService.isPending(this.order);

        this.qr = this.order.payment?.[0]?.qrcode;

        this.mappedOrder = this.mapToOrder(this.order);

        if (this.order.suborders.length) {
            this.store.dispatch(SetSubOrderSelected({ subOrderSelected: Number(this.order.suborders[0].id) }));
        }

        this.storeOrderTicketUrl$ = combineLatest([this.store.select(getCmsConfigValue('ticketless')), this.fromOnline$]).pipe(
            switchMap(([ticketless, fromOnline]) => {
                if (!fromOnline && ticketless) {
                    this.pdfList = true;
                    // TODO ECOMDUTI-21039 [TechDebt][SSR] -  Tipo de retorno incosistente: Observable<ListTicketlessDocument[]>
                    return this.orderService.getPdfListTicketless(this.order.receiptUID);
                }

                return this.getTicketUrl(
                    (this.order as any).stlocId,
                    (this.order as any).operationNumber,
                    (this.order as any).idCaja
                );
            }),
            shareReplay(1)
        );

        this.setIsReturnable();

        this.checkIsCancellable();
    }

    private setIsReturnable(): void {
        combineLatest([this.store.select(getAllowedReturnStatus), this.store.select(getShowReturnRequest)])
            .pipe(take(1))
            .subscribe(([allowedReturnStatus, showReturnRequest]) => {
                if (allowedReturnStatus.includes(this.order.status) && showReturnRequest && this.order.shipping?.name !== 'Mail') {
                    this.isReturnable = this.order.isReturnableDate === 1;
                }
            });
    }

    private getTicketUrl(stlocId, operationNumber, idCaja) {
        if (!stlocId || !operationNumber || !idCaja) {
            return of(null);
        }

        return this.store.select(getStoreAndLanguage).pipe(
            map((storeAndLanguage) => {
                let baseUrl = `${this.configService.getConfigUrl<string>('baseUrl')}/itxrest/${this.window.inditex.iRestVersion}/order/store/${storeAndLanguage.storeId
                }`;
                return `${baseUrl}/customer-stloc-order/${stlocId}/${operationNumber}/${idCaja}/ticket?languageId=${storeAndLanguage.languageId}fileName=A9450_244`;
            })
        );
    }

    public requestPrintedEgui() {
        this.store.dispatch(PrintEgui({ eguiModal: false }));
    }

    public handleChangeOrderSelected(subOrderId: number) {
        this.store.dispatch(SetSubOrderSelected({ subOrderSelected: subOrderId }));
    }

    public repay() {
        this.store.dispatch(RepayOrder());
    }

    public orderRefundDialog(order: Partial<Order>) {
        this.store.dispatch(OpenOrderRefundModal({ order }));
    }

    public openAllDocuments(storeOrderTicketUrl: ListTicketlessDocument[]) {
        if (storeOrderTicketUrl) {
            storeOrderTicketUrl.forEach((document) => {
                if (document.uri) {
                    this.window.open(document.uri, '_blank');
                }
            });
        }
    }

    public cancelOrder(hasSubOrders: boolean) {
        this.store.dispatch(OpenCancelOrderModal({ hasSubOrders }));
    }

    public mapToOrder(order: Partial<Order>): Order {
        return order as Order;
    }

    private checkIsCancellable() {
        const isCancellable = this.itemOrderService.isCancellableOrder(this.order);
        let allOrderIsCancellable = false;

        if (isCancellable) {
            allOrderIsCancellable = this.hasSubOrders ? this.allOrderIsCancellable() : true;
        }

        this.isCancellableSubOrder = isCancellable && this.hasSubOrders;
        this.isCancellableAllOrder = allOrderIsCancellable;
    }

    private allOrderIsCancellable() {
        let allOrderIsCancellable = true;

        if (this.order.suborders) {
            this.order.suborders.forEach((suborder) => {
                if (!this.itemOrderService.isCancellableOrder(suborder)) {
                    allOrderIsCancellable = false;
                }
            });
        }

        return allOrderIsCancellable;
    }

    public returnOrder(order: Partial<Order>) {
        this.store.select(getCmsConfigValue('return_recomendation')).pipe(
            take(1),
        ).subscribe((returnRecomendation) => {
            if (returnRecomendation) {
                SidebarPublicPort(this.store).openSidebarCustom({
                    component: SidebarPublicModels.sidebarCustomComponents.ORDER_RETURN_RECOM,
                    initializeFunction: undefined,
                    data: { order },
                });
            } else {
                this.store.dispatch(GoToInternalPage({ url: `my-account/returns/order-returns?order=${order.id}` }));
            }
        });
    }

    public changeOrder(order) {
        this.store.dispatch(GoToInternalPage({ url: `my-account/order-change-return-selector?order=${order.id}` }));
    }

    public openGiftTicketModal() {
        this.store.dispatch(OpenGiftTicketModal());
    }

    private showGiftTicketModal(): Observable<boolean> {
        return combineLatest([
            this.store.select(showGiftTicket),
            this.store.select(selectItemsSubOrderSelected),
            this.store.select(selectDetailOrder),
        ]).pipe(
            map(([show, items, order]) =>
                show
                && !!this.getItemsGiftTicket(items).length
                && order.giftTicket === '1'
                && !['X', 'N', 'A', 'T'].includes(order.status))
        );
    }

    private getItemsGiftTicket(items: Item[]) {
        return items.filter((item) => item.type !== 'VirtualGiftCard' && !item.isCustomized);
    }
}
